import { FormEvent, useEffect, useId, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api } from "../../api/client";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { BreedMultiSelect } from "../../components/forms/BreedMultiSelect";
import { Field } from "../../components/forms/Field";
import { FormModal } from "../../components/modals/FormModal";
import { apiError } from "../../lib/apiError";
import { formatGroupBreed } from "../../lib/breeds";
import { passwordFieldProps } from "../../lib/inputPrivacy";
import { formatDate, todayIso } from "../../lib/format";
import type { BirthSeason, BreedingSeasonDetail } from "../../types";

type Mode = "breeding" | "births";

const EMPTY = {
  name: "",
  breeds: [] as string[],
  start_date: todayIso(),
  gestation_days: "150",
  note: "",
  weight_count: "0",
  password: "",
};

const EMPTY_EDIT = {
  name: "",
  start_date: "",
  gestation_days: "150",
  note: "",
  weight_count: "0",
  password: "",
};

function parseWeightCount(raw: string): number | null {
  const value = Number(raw);
  if (!Number.isFinite(value) || !Number.isInteger(value) || value < 0 || value > 20) {
    return null;
  }
  return value;
}

export function ReproductionHubPage() {
  const navigate = useNavigate();
  const fieldNs = useId().replace(/:/g, "");
  const [groups, setGroups] = useState<BirthSeason[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [mode, setMode] = useState<Mode>("breeding");
  const [create, setCreate] = useState(EMPTY);
  const [editGroup, setEditGroup] = useState<BirthSeason | null>(null);
  const [edit, setEdit] = useState(EMPTY_EDIT);
  const [archiveGroup, setArchiveGroup] = useState<BirthSeason | null>(null);
  const [breeds, setBreeds] = useState<string[]>([]);
  const [hasUnspecifiedBreeds, setHasUnspecifiedBreeds] = useState(false);

  async function loadGroups() {
    const result = await api.GET("/api/birth-seasons", {
      params: { query: { scope: "active" } },
    });
    if (result.error) {
      setError(apiError(result.error, "Nie udało się wczytać grup rozrodu."));
      return;
    }
    setError(null);
    setGroups((result.data as BirthSeason[]) ?? []);
  }

  async function loadBreeds() {
    const result = await api.GET("/api/birth-seasons/breeds");
    if (result.error) {
      return;
    }
    const data = result.data as { breeds?: string[]; has_unspecified?: boolean } | null;
    setBreeds(data?.breeds ?? []);
    setHasUnspecifiedBreeds(Boolean(data?.has_unspecified));
  }

  useEffect(() => {
    void loadGroups();
    void loadBreeds();
  }, []);

  function closeCreate() {
    setShowCreate(false);
    setCreate({ ...EMPTY, start_date: todayIso() });
    setMode("breeding");
  }

  function openEdit(group: BirthSeason) {
    setEditGroup(group);
    setEdit({
      name: group.name,
      start_date: group.start_date?.slice(0, 10) || "",
      gestation_days: String(group.gestation_days ?? 150),
      note: group.note || "",
      weight_count: String(group.weight_count ?? 0),
      password: "",
    });
  }

  function closeEdit() {
    setEditGroup(null);
    setEdit(EMPTY_EDIT);
  }

  async function onEdit(event: FormEvent) {
    event.preventDefault();
    if (!editGroup?.breeding_season_id) {
      setError("Nie można edytować tej grupy.");
      return;
    }
    const name = edit.name.trim();
    if (!name) {
      setError("Podaj nazwę grupy.");
      return;
    }
    const gestation = Number(edit.gestation_days);
    if (!Number.isFinite(gestation) || gestation < 1 || gestation > 400) {
      setError("Podaj czas ciąży w dniach (1–400).");
      return;
    }
    const weightCount = parseWeightCount(edit.weight_count);
    if (weightCount == null) {
      setError("Podaj liczbę ważeń jagniąt (0–20).");
      return;
    }
    setBusy(true);
    const updated = await api.PATCH("/api/breeding-seasons/{season_id}", {
      params: { path: { season_id: editGroup.breeding_season_id } },
      body: {
        name,
        start_date: edit.start_date || null,
        gestation_days: gestation,
        note: edit.note.trim() || null,
        weight_count: weightCount,
        password: edit.password || null,
      } as never,
    });
    setBusy(false);
    if (updated.error) {
      setError(apiError(updated.error, "Nie udało się zapisać grupy."));
      return;
    }
    closeEdit();
    await loadGroups();
  }

  function closeArchive() {
    setArchiveGroup(null);
  }

  async function onArchive(event: FormEvent) {
    event.preventDefault();
    if (!archiveGroup) {
      return;
    }
    const archivedId = archiveGroup.id;
    setBusy(true);
    const result = await api.POST("/api/birth-seasons/{season_id}/archive", {
      params: { path: { season_id: archivedId } },
      body: {},
    });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się zarchiwizować grupy."));
      return;
    }
    // Drop immediately so UI does not wait on a follow-up list reload race.
    setGroups((current) => current.filter((group) => group.id !== archivedId));
    closeArchive();
    await loadGroups();
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    if (!create.name.trim()) {
      setError("Podaj nazwę grupy.");
      return;
    }
    const weightCount = parseWeightCount(create.weight_count);
    if (weightCount == null) {
      setError("Podaj liczbę ważeń jagniąt (0–20).");
      return;
    }
    setBusy(true);
    if (mode === "breeding") {
      const created = await api.POST("/api/breeding-seasons", {
        body: {
          name: create.name.trim(),
          breeds: create.breeds.length ? create.breeds : null,
          start_date: create.start_date || null,
          gestation_days: Number(create.gestation_days) || 150,
          note: create.note.trim() || null,
          weight_count: weightCount,
          password: create.password || null,
          members: [],
        } as never,
      });
      setBusy(false);
      if (created.error) {
        setError(apiError(created.error, "Nie udało się utworzyć grupy."));
        return;
      }
      const season = created.data as BreedingSeasonDetail;
      closeCreate();
      navigate(`/reproduction/breeding?seasonId=${season.id}&editMembers=1`);
      return;
    }

    const created = await api.POST("/api/birth-seasons", {
      body: {
        name: create.name.trim(),
        breeds: create.breeds.length ? create.breeds : null,
        weight_count: weightCount,
      },
    });
    setBusy(false);
    if (created.error) {
      setError(apiError(created.error, "Nie udało się utworzyć grupy."));
      return;
    }
    const season = created.data as BirthSeason;
    closeCreate();
    navigate(`/reproduction/births?seasonId=${season.id}&editMembers=1`);
  }

  return (
    <main className="page" data-testid="reproduction-hub">
      <header className="page__header page__header--row">
        <div>
          <p className="page__kicker">Rozród</p>
          <h1>Aktywne grupy</h1>
        </div>
        <button
          type="button"
          className="btn"
          data-testid="reproduction-create"
          onClick={() => setShowCreate(true)}
        >
          Nowa grupa
        </button>
      </header>

      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />

      <FormModal
        open={showCreate}
        onClose={closeCreate}
        title="Nowa grupa rozrodu"
        testId="reproduction-create-form"
        skipHistory
        onSubmit={onCreate}
      >
        <Field label="Nazwa">
          <input
            className="input"
            value={create.name}
            onChange={(event) => setCreate({ ...create, name: event.target.value })}
            required
            data-testid="breeding-name"
          />
        </Field>
        <Field label="Rasy">
          <BreedMultiSelect
            breeds={breeds}
            hasUnspecified={hasUnspecifiedBreeds}
            selected={create.breeds}
            onChange={(breedsSelected) =>
              setCreate({ ...create, breeds: breedsSelected })
            }
            testId="reproduction-breed-select"
          />
        </Field>
        <fieldset className="reproduction-mode">
          <legend className="reproduction-mode__legend">Typ grupy</legend>
          <label className="reproduction-mode__option">
            <input
              type="radio"
              name="reproduction-mode"
              checked={mode === "breeding"}
              onChange={() => setMode("breeding")}
              data-testid="reproduction-mode-breeding"
            />
            Stanówka i wykoty
          </label>
          <label className="reproduction-mode__option">
            <input
              type="radio"
              name="reproduction-mode"
              checked={mode === "births"}
              onChange={() => setMode("births")}
              data-testid="reproduction-mode-births"
            />
            Tylko wykoty
          </label>
        </fieldset>
        <Field label="Liczba ważeń jagniąt (0 = bez ważeń)">
          <input
            className="input"
            type="number"
            min={0}
            max={20}
            step={1}
            value={create.weight_count}
            onChange={(event) =>
              setCreate({ ...create, weight_count: event.target.value })
            }
            data-testid="reproduction-weight-count"
            required
          />
        </Field>
        <p className="muted reproduction-weight-hint">
          Ustaw ile razy ważysz jagnięta (np. 3). Potem w Wykotach możesz dodać reguły
          terminów. 0 wyłącza ważenia.
        </p>
        {mode === "breeding" ? (
          <>
            <div className="form__row">
              <Field label="Data startu">
                <input
                  className="input"
                  type="date"
                  value={create.start_date}
                  onChange={(event) =>
                    setCreate({ ...create, start_date: event.target.value })
                  }
                />
              </Field>
              <Field label="Szacowany czas ciąży (dni)">
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={400}
                  value={create.gestation_days}
                  onChange={(event) =>
                    setCreate({ ...create, gestation_days: event.target.value })
                  }
                  data-testid="breeding-gestation-days"
                />
              </Field>
            </div>
            <Field label="Notatka (opcjonalnie, max 100 znaków)">
              <input
                className="input"
                value={create.note}
                maxLength={100}
                onChange={(event) => setCreate({ ...create, note: event.target.value })}
                data-testid="breeding-note"
                placeholder="Krótki opis grupy…"
              />
            </Field>
            <Field label="Hasło administratora (opcjonalnie)">
              <input
                className="input"
                type="password"
                value={create.password}
                onChange={(event) =>
                  setCreate({ ...create, password: event.target.value })
                }
                data-testid="breeding-create-password"
                {...passwordFieldProps(`${fieldNs}-breeding-create-password`)}
              />
            </Field>
          </>
        ) : null}
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={closeCreate}>
            Anuluj
          </button>
          <button
            className="btn"
            type="submit"
            disabled={busy}
            data-testid="breeding-create-save"
          >
            Utwórz
          </button>
        </div>
      </FormModal>

      <FormModal
        open={editGroup != null}
        onClose={closeEdit}
        title={editGroup ? `Edytuj grupę — ${editGroup.name}` : "Edytuj grupę"}
        testId="reproduction-edit-form"
        onSubmit={onEdit}
      >
        <p className="muted reproduction-edit__meta">
          Typ i rasy nie podlegają zmianie
          {formatGroupBreed(editGroup?.breed)
            ? ` · ${formatGroupBreed(editGroup?.breed)}`
            : ""}
          {editGroup
            ? ` · ${
                editGroup.include_mating ? "Stanówka i wykoty" : "Tylko wykoty"
              }`
            : ""}
        </p>
        <Field label="Nazwa">
          <input
            className="input"
            value={edit.name}
            onChange={(event) => setEdit({ ...edit, name: event.target.value })}
            required
            maxLength={120}
            data-testid="reproduction-edit-name"
          />
        </Field>
        <div className="form__row">
          <Field label="Data startu">
            <input
              className="input"
              type="date"
              value={edit.start_date}
              onChange={(event) =>
                setEdit({ ...edit, start_date: event.target.value })
              }
              data-testid="reproduction-edit-start-date"
            />
          </Field>
          <Field label="Szacowany czas ciąży (dni)">
            <input
              className="input"
              type="number"
              min={1}
              max={400}
              value={edit.gestation_days}
              onChange={(event) =>
                setEdit({ ...edit, gestation_days: event.target.value })
              }
              data-testid="reproduction-edit-gestation-days"
            />
          </Field>
        </div>
        <Field label="Notatka (opcjonalnie, max 100 znaków)">
          <input
            className="input"
            value={edit.note}
            maxLength={100}
            onChange={(event) => setEdit({ ...edit, note: event.target.value })}
            data-testid="reproduction-edit-note"
            placeholder="Krótki opis grupy…"
          />
        </Field>
        <Field label="Liczba ważeń jagniąt (0 = bez ważeń)">
          <input
            className="input"
            type="number"
            min={0}
            max={20}
            step={1}
            value={edit.weight_count}
            onChange={(event) =>
              setEdit({ ...edit, weight_count: event.target.value })
            }
            data-testid="reproduction-edit-weight-count"
            required
          />
        </Field>
        <p className="muted reproduction-weight-hint">
          Po włączeniu ważeń ustaw reguły terminów w Wykotach → Reguły ważenia.
        </p>
        <Field label="Hasło administratora (opcjonalnie)">
          <input
            className="input"
            type="password"
            value={edit.password}
            onChange={(event) =>
              setEdit({ ...edit, password: event.target.value })
            }
            data-testid="reproduction-edit-password"
            {...passwordFieldProps(`${fieldNs}-reproduction-edit-password`)}
          />
        </Field>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={closeEdit}>
            Anuluj
          </button>
          <button
            className="btn"
            type="submit"
            disabled={busy}
            data-testid="reproduction-edit-save"
          >
            Zapisz
          </button>
        </div>
      </FormModal>

      <FormModal
        open={archiveGroup != null}
        onClose={closeArchive}
        title={archiveGroup ? `Archiwizuj — ${archiveGroup.name}` : "Archiwizuj grupę"}
        testId="reproduction-archive-form"
        skipHistory
        onSubmit={onArchive}
      >
        <p className="muted">
          Grupa zniknie z aktywnej listy. Możesz ją później przywrócić w archiwum.
        </p>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={closeArchive} disabled={busy}>
            Anuluj
          </button>
          <button
            className="btn"
            type="submit"
            disabled={busy}
            data-testid="reproduction-archive-confirm"
          >
            Archiwizuj
          </button>
        </div>
      </FormModal>

      {groups.length === 0 ? (
        <p className="muted">Brak aktywnych grup — utwórz pierwszą.</p>
      ) : (
        <section className="panel">
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Grupa</th>
                  <th>Typ</th>
                  <th>Data startu</th>
                  <th>Ciąża</th>
                  <th>Notatka</th>
                  <th>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.id}>
                    <td>
                      {group.name}
                      {formatGroupBreed(group.breed)
                        ? ` · ${formatGroupBreed(group.breed)}`
                        : ""}
                    </td>
                    <td>
                      {group.include_mating
                        ? "Stanówka i wykoty"
                        : "Tylko wykoty"}
                    </td>
                    <td>{formatDate(group.start_date)}</td>
                    <td>
                      {group.gestation_days != null
                        ? `${group.gestation_days} dni`
                        : "—"}
                    </td>
                    <td className="tbl__note">{group.note || "—"}</td>
                    <td>
                      <div className="actions">
                        {group.breeding_season_id ? (
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            data-testid={`reproduction-edit-${group.id}`}
                            onClick={() => openEdit(group)}
                          >
                            Edytuj
                          </button>
                        ) : null}
                        {group.include_mating && group.breeding_season_id ? (
                          <Link
                            className="btn btn--ghost btn--sm"
                            to={`/reproduction/breeding?seasonId=${group.breeding_season_id}`}
                          >
                            Stanówka
                          </Link>
                        ) : null}
                        <Link
                          className="btn btn--ghost btn--sm"
                          to={`/reproduction/births?seasonId=${group.id}`}
                        >
                          Wykoty
                        </Link>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          data-testid={`reproduction-archive-${group.id}`}
                          disabled={busy}
                          onClick={() => setArchiveGroup(group)}
                        >
                          Archiwizuj
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </main>
  );
}
